import { useState, useEffect, useCallback } from "react";

import { useAppSelector } from "~/app/store";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import { useGetAutostoreGridStatusQuery } from "~/redux/warehouse/autostoreGrid.hooks";

const useWarehouseResponseTimes = (interval = 1000, maxPoints = 600) => {
  const [responseTimes, setResponseTimes] = useState<number[]>([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [skippedFirst, setSkippedFirst] = useState(false);

  const autostoreGridId =
    useAppSelector(selectWorkstationAutostoreGridId) || "";

  const { data, isFetching } = useGetAutostoreGridStatusQuery(autostoreGridId, {
    pollingInterval: isRunning ? interval : 0,
    skip: !isRunning || !autostoreGridId
  });

  useEffect(() => {
    if (isFetching) {
      performance.clearResourceTimings();
    }
  }, [isFetching]);

  useEffect(() => {
    if (!isRunning || isFetching) return;

    const entries = performance.getEntriesByType("resource");
    const getRequest = entries.find((entry) =>
      entry.name.includes("system-status")
    );

    if (getRequest) {
      const responseTime =
        (getRequest as PerformanceResourceTiming).responseEnd -
        (getRequest as PerformanceResourceTiming).startTime;
      const newTime = Math.round(responseTime);

      if (!skippedFirst) {
        setSkippedFirst(true);
        return;
      }

      setResponseTimes((prevTimes) => {
        let newTimes = [...prevTimes, newTime];
        if (newTimes.length > maxPoints) {
          newTimes = newTimes.slice(newTimes.length - maxPoints);
        }
        const avgTime = newTimes.reduce((a, b) => a + b, 0) / newTimes.length;
        setAverageResponseTime(Math.round(avgTime));
        return newTimes;
      });
    }
  }, [data, isFetching, isRunning, maxPoints, skippedFirst]);

  const toggleRunning = useCallback(() => {
    setIsRunning((prev) => !prev);
  }, []);

  const clearData = useCallback(() => {
    setResponseTimes([]);
    setAverageResponseTime(0);
    setSkippedFirst(false);
  }, []);

  return {
    responseTimes,
    averageResponseTime,
    isRunning,
    toggleRunning,
    clearData
  };
};

export default useWarehouseResponseTimes;
